/**
 * Warren-themed Animation Presets
 *
 * Playful, bouncy animations that match Warren the beaver's friendly personality.
 * All animations are designed to be:
 * - Spring-based for natural, playful motion
 * - GPU-accelerated (using transform and opacity)
 * - Accessible (respects prefers-reduced-motion)
 * - Performance-optimized
 */

import type { Transition } from 'framer-motion';

/**
 * Warren's signature spring configuration - bouncy and playful!
 * Perfect for buttons, cards, and interactive elements
 */
export const warrenSpring: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 25,
  mass: 0.8,
};

/**
 * Gentler spring for larger elements or page transitions
 */
export const gentleSpring: Transition = {
  type: 'spring',
  stiffness: 260,
  damping: 30,
  mass: 1,
};

/**
 * Quick, snappy spring for micro-interactions
 */
export const snappySpring: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 20,
  mass: 0.5,
};

/**
 * Smooth tween for predictable animations (loading states, etc.)
 */
export const smoothTween: Transition = {
  type: 'tween',
  duration: 0.3,
  ease: [0.4, 0.0, 0.2, 1], // Material Design easing
};

/**
 * Fast tween for quick transitions
 */
export const fastTween: Transition = {
  type: 'tween',
  duration: 0.2,
  ease: [0.4, 0.0, 0.2, 1],
};

/**
 * Slow, deliberate tween for emphasis
 */
export const slowTween: Transition = {
  type: 'tween',
  duration: 0.5,
  ease: [0.4, 0.0, 0.2, 1],
};

/**
 * Button hover animation preset
 * Gentle scale up with bounce
 */
export const buttonHover = {
  scale: 1.05,
  y: -2,
  transition: warrenSpring,
};

/**
 * Button tap animation preset
 * Quick scale down for tactile feedback
 */
export const buttonTap = {
  scale: 0.98,
  y: 0,
  transition: snappySpring,
};

/**
 * Card hover animation preset
 * Float up with shadow increase (shadow handled via CSS)
 */
export const cardHover = {
  y: -8,
  transition: warrenSpring,
};

/**
 * Card tap animation preset
 */
export const cardTap = {
  scale: 0.99,
  transition: snappySpring,
};

/**
 * Icon hover animation preset
 * Playful rotation and scale
 */
export const iconHover = {
  scale: 1.15,
  rotate: 5,
  transition: warrenSpring,
};

/**
 * Stagger children animation preset
 * For lists, grids, etc.
 */
export const staggerChildren = {
  staggerChildren: 0.07,
  delayChildren: 0.1,
};

/**
 * Faster stagger for large lists
 */
export const fastStagger = {
  staggerChildren: 0.03,
  delayChildren: 0.05,
};

/**
 * Page transition duration
 */
export const pageTransitionDuration = 0.3;

/**
 * Modal/Dialog transition configuration
 */
export const modalTransition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
};

/**
 * Shake animation for form errors
 * Creates a playful "no no" motion
 */
export const errorShake = {
  x: [0, -10, 10, -10, 10, 0],
  transition: {
    duration: 0.5,
    ease: [0.4, 0.0, 0.6, 1.0],
  },
};

/**
 * Success pulse animation
 * Gentle scale pulse for positive feedback
 */
export const successPulse = {
  scale: [1, 1.1, 1],
  transition: {
    duration: 0.6,
    ease: [0.4, 0.0, 0.6, 1.0],
  },
};

/**
 * Loading bounce animation
 * Playful up-down motion for loading indicators
 */
export const loadingBounce = {
  y: [0, -10, 0],
  transition: {
    duration: 0.6,
    ease: [0.4, 0.0, 0.6, 1.0],
    repeat: Infinity,
  },
};

/**
 * Fade in animation preset
 */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: smoothTween,
};

/**
 * Scale in animation preset (for modals, dialogs)
 */
export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
  transition: modalTransition,
};

/**
 * Slide in from top preset
 */
export const slideInFromTop = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: warrenSpring,
};

/**
 * Slide in from bottom preset
 */
export const slideInFromBottom = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: warrenSpring,
};

/**
 * Slide in from left preset
 */
export const slideInFromLeft = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: warrenSpring,
};

/**
 * Slide in from right preset
 */
export const slideInFromRight = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
  transition: warrenSpring,
};

/**
 * Expand animation for accordions, collapsibles
 */
export const expand = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: gentleSpring,
};

/**
 * Check if user prefers reduced motion
 * Returns true if animations should be disabled
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Wrapper to disable animations if user prefers reduced motion
 * @param animation - The animation object to potentially disable
 * @returns The animation object or a no-motion version
 */
export const respectMotionPreference = <T extends Record<string, unknown>>(animation: T): T => {
  if (prefersReducedMotion()) {
    // Return instant transitions
    return {
      ...animation,
      transition: { duration: 0 },
    };
  }
  return animation;
};
